import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Button,
  CircularProgress,
  Tooltip,
  Pagination,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Grid,
  Alert,
  Snackbar,
  useTheme,
  alpha,
  Checkbox
} from '@mui/material';
import {
  ArrowBack,
  NotificationsActive,
  Search,
  FilterList,
  MarkEmailRead,
  Delete,
  Refresh,
  CheckCircleOutline,
  ErrorOutline,
  ChevronRight
} from '@mui/icons-material';
import { useAuthToken } from "../TokenContext";
import moment from 'moment';

// Constants
const API_ENDPOINTS = {
  NOTIFICATIONS: process.env.REACT_APP_TALENT_CANDIDATENOTIFICATIONS,
  MARK_READ: process.env.REACT_APP_TALENT_NOTIFICATIONREAD
};

const ITEMS_PER_PAGE = 15;

const NotificationsHistory = () => {
  const theme = useTheme();
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const token = useAuthToken();
  
  // State management
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all'); // 'all', 'read', 'unread'
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  
  // Function to fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!token || !user?.data?.[0]?.c_id) {
      console.log('Token or user data is missing');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.NOTIFICATIONS, {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ candidate_id: user.data[0].c_id }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const { data } = await response.json();
      
      if (!data || !Array.isArray(data)) {
        setNotifications([]);
        setFilteredNotifications([]);
        return;
      }
      
      const processedNotifications = data
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .map(notification => ({
          ...notification,
          timeAgo: moment(notification.createdAt).fromNow(),
          formattedDate: moment(notification.createdAt).format('MMM DD, YYYY [at] h:mm A'),
          read: notification.notification_read === 1,
          // Ensure podID and podName are properly assigned
          podID: notification.podID || null,
          podName: notification.podName || ''
        }));

      setNotifications(processedNotifications);
      applyFilters(processedNotifications, filter, searchTerm);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Could not load notifications. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [token, user, filter, searchTerm]);

  // Initial fetch when component mounts
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Apply filters and search
  const applyFilters = (notifs, filterType, search) => {
    let filtered = [...notifs];
    
    // Apply read/unread filter
    if (filterType === 'read') {
      filtered = filtered.filter(notification => notification.read);
    } else if (filterType === 'unread') {
      filtered = filtered.filter(notification => !notification.read);
    }
    
    // Apply search term
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(notification => 
        notification.message?.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredNotifications(filtered);
    // Reset to first page when filters change
    setPage(1);
  };

  // Handle filter change
  const handleFilterChange = (event) => {
    const newFilter = event.target.value;
    setFilter(newFilter);
    applyFilters(notifications, newFilter, searchTerm);
  };

  // Handle search
  const handleSearchChange = (event) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    applyFilters(notifications, filter, newSearchTerm);
  };

  // Handle pagination change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    // Mark notification as read if it's not already read
    if (!notification.read) {
      handleMarkAsRead([notification.id]);
    }

    // Navigate based on notification content/type
    if (notification.message.toLowerCase().includes('task') && notification.podID) {
      // Navigate to task management with necessary pod info
      // Set viewTab: true to automatically switch to the View Tasks tab
      history.push({
        pathname: '/Createtasktalent',  // This route is handled by CreateTaskTalent component
        state: {
          podid: notification.podID,
          podname: notification.podName || '',
          viewTab: true // This signals to show the View Tasks tab
        }
      });
    } else if (notification.message.toLowerCase().includes('pod activity')) {
      // Navigate to pod activity page
      history.push({
          pathname: '/Podactivitytalent',
          state: {
            podid: notification.podID,
              viewTab: true  
          }
          });
    } else {
      // Default navigation
      history.push('/candidate-dashboard');
    }
  };

  // Mark notifications as read
  const handleMarkAsRead = async (notificationIds) => {
    if (notificationIds.length === 0) return;

    try {
      const response = await fetch(API_ENDPOINTS.MARK_READ, {
        method: 'PUT',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notification_ids: notificationIds }),
      });

      if (!response.ok) {
        throw new Error('Failed to mark notifications as read');
      }

      // Update local state
      setNotifications(prev => 
        prev.map(note => 
          notificationIds.includes(note.id) ? { ...note, read: true } : note
        )
      );
      
      // Also update filtered notifications
      setFilteredNotifications(prev => 
        prev.map(note => 
          notificationIds.includes(note.id) ? { ...note, read: true } : note
        )
      );
      
      setSnackbar({
        open: true,
        message: `${notificationIds.length} notification${notificationIds.length > 1 ? 's' : ''} marked as read`,
        severity: 'success'
      });
      
      // Clear selected notifications
      setSelectedNotifications([]);
      
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      setSnackbar({
        open: true,
        message: 'Could not mark notifications as read',
        severity: 'error'
      });
    }
  };

  // Mark all filtered notifications as read
  const handleMarkAllAsRead = async () => {
    const unreadIds = filteredNotifications
      .filter(note => !note.read)
      .map(note => note.id);
    
    if (unreadIds.length === 0) {
      setSnackbar({
        open: true,
        message: 'No unread notifications to mark as read',
        severity: 'info'
      });
      return;
    }

    handleMarkAsRead(unreadIds);
  };

  // Toggle notification selection
  const toggleNotificationSelection = (notificationId) => {
    setSelectedNotifications(prevSelected => {
      if (prevSelected.includes(notificationId)) {
        return prevSelected.filter(id => id !== notificationId);
      } else {
        return [...prevSelected, notificationId];
      }
    });
  };

  // Mark selected notifications as read
  const handleMarkSelectedAsRead = () => {
    if (selectedNotifications.length === 0) {
      setSnackbar({
        open: true,
        message: 'No notifications selected',
        severity: 'info'
      });
      return;
    }
    
    handleMarkAsRead(selectedNotifications);
  };

  // Handle snackbar close
  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Calculate pagination
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedNotifications = filteredNotifications.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredNotifications.length / ITEMS_PER_PAGE);

  // Get priority color based on notification type
  const getPriorityColor = (notification) => {
    if (notification.type === 'urgent' || notification.priority === 'high') {
      return '#f44336'; // Red for urgent
    } else if (notification.type === 'important' || notification.priority === 'medium') {
      return '#ff9800'; // Orange for important
    }
    return '#2196f3'; // Default blue
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          borderRadius: 2, 
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <IconButton 
            onClick={() => history.goBack()} 
            sx={{ color: 'white' }}
            aria-label="Go back"
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsActive /> Notification History
          </Typography>
        </Box>

        {/* Filters Section */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search notifications..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.background.paper, 0.9)
                    }
                  }
                }}
              />
            </Grid>
            
            {/* Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="notification-filter-label">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FilterList fontSize="small" />
                    Filter
                  </Box>
                </InputLabel>
                <Select
                  labelId="notification-filter-label"
                  value={filter}
                  onChange={handleFilterChange}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FilterList fontSize="small" />
                      Filter
                    </Box>
                  }
                  sx={{
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.background.paper, 0.9)
                    }
                  }}
                >
                  <MenuItem value="all">All Notifications</MenuItem>
                  <MenuItem value="unread">Unread</MenuItem>
                  <MenuItem value="read">Read</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Actions */}
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  startIcon={<MarkEmailRead />}
                  onClick={handleMarkSelectedAsRead}
                  disabled={selectedNotifications.length === 0}
                  sx={{ borderRadius: 2 }}
                >
                  Mark Selected
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  startIcon={<MarkEmailRead />}
                  onClick={handleMarkAllAsRead}
                  sx={{ borderRadius: 2 }}
                >
                  Mark All Read
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Refresh />}
                  onClick={fetchNotifications}
                  sx={{ borderRadius: 2 }}
                >
                  Refresh
                </Button>
              </Box>
            </Grid>
          </Grid>
          
          {/* Filter Chips */}
          {(filter !== 'all' || searchTerm) && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {filter !== 'all' && (
                <Chip 
                  label={`Status: ${filter === 'read' ? 'Read' : 'Unread'}`} 
                  onDelete={() => {
                    setFilter('all');
                    applyFilters(notifications, 'all', searchTerm);
                  }}
                  color="primary"
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
              )}
              {searchTerm && (
                <Chip 
                  label={`Search: ${searchTerm}`} 
                  onDelete={() => {
                    setSearchTerm('');
                    applyFilters(notifications, filter, '');
                  }}
                  color="primary"
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
              )}
            </Box>
          )}
          
          {/* Stats */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {paginatedNotifications.length} of {filteredNotifications.length} notifications
              {filteredNotifications.length !== notifications.length && 
                ` (filtered from ${notifications.length} total)`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {notifications.filter(n => !n.read).length} unread
            </Typography>
          </Box>
        </Box>

        {/* Notification List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
            <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <ErrorOutline color="error" sx={{ fontSize: 48, mb: 2 }} />
            <Typography color="error" gutterBottom>{error}</Typography>
            <Button 
              variant="contained" 
              onClick={fetchNotifications}
              startIcon={<Refresh />}
              sx={{ mt: 2 }}
            >
              Try Again
            </Button>
          </Box>
        ) : paginatedNotifications.length === 0 ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <CheckCircleOutline sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2, opacity: 0.6 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No notifications found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filter !== 'all' || searchTerm 
                ? 'Try changing your filters or search term'
                : 'You don\'t have any notifications yet'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {paginatedNotifications.map((notification) => {
              const borderColor = getPriorityColor(notification);
              const isSelected = selectedNotifications.includes(notification.id);
              
              return (
                <React.Fragment key={notification.id}>
                  <ListItem
                    sx={{
                      py: 2,
                      px: 3,
                      display: 'flex',
                      alignItems: 'center',
                      borderLeft: `4px solid ${borderColor}`,
                      backgroundColor: isSelected 
                        ? alpha(theme.palette.primary.main, 0.1)
                        : notification.read 
                          ? 'inherit'
                          : alpha(theme.palette.primary.main, 0.05),
                      '&:hover': {
                        backgroundColor: isSelected
                          ? alpha(theme.palette.primary.main, 0.15)
                          : alpha(theme.palette.primary.main, 0.08),
                        cursor: 'pointer'
                      },
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    {/* Checkbox for selection */}
                    <Checkbox
                      checked={isSelected}
                      onChange={() => toggleNotificationSelection(notification.id)}
                      onClick={(e) => e.stopPropagation()}
                      color="primary"
                      sx={{ mr: 1 }}
                    />
                    
                    {/* Notification Content */}
                    <Box 
                      sx={{ 
                        flex: 1,
                        overflow: 'hidden'
                      }}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: notification.read ? 'normal' : 'bold',
                          mb: 0.5,
                          color: theme.palette.text.primary
                        }}
                      >
                        {notification.message}
                      </Typography>
                      
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          display: 'block'
                        }}
                      >
                        {notification.formattedDate} ({notification.timeAgo})
                      </Typography>
                      
                      {/* Pod info if available */}
                      {notification.podName && (
                        <Chip
                          label={notification.podName}
                          size="small"
                          sx={{ 
                            mt: 1, 
                            height: 24, 
                            fontSize: '0.7rem',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            borderRadius: 1
                          }}
                        />
                      )}
                    </Box>
                    
                    {/* Action Icon */}
                    <ChevronRight color="action" sx={{ ml: 1 }} />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              );
            })}
          </List>
        )}

        {/* Pagination */}
        {filteredNotifications.length > ITEMS_PER_PAGE && (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              p: 2,
              borderTop: `1px solid ${theme.palette.divider}`
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              size="medium"
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 1
                }
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default NotificationsHistory;